/**
 * Лента миниатюр слайдов: картинку держим только у тех, что видно.
 *
 * Раньше в ленте было по `<img>` на каждый слайд, и все они брали тот же файл,
 * что и большой слайд — сторона 2560 px, это ~15 МБ в распакованном виде на
 * страницу. На презентации в 30 слайдов браузер пытался держать сотни
 * мегабайт картинок. iOS столько не даёт: Safari гасит уже распакованные
 * изображения, и вместо презентации остаётся чёрный экран.
 *
 * Теперь в ленту идёт не исходник, а уменьшенная копия ([[slideThumbs]]):
 * сторона 320 px вместо 2560. Поэтому картинок на экране может быть сколько
 * угодно — потолок ниже остался только страховкой от совсем дикого случая.
 *
 * Видимость считаем по прокрутке самой ленты, а не через IntersectionObserver:
 * лента на доске живёт внутри слоя с преобразованиями камеры, и там наблюдатель
 * пересечений ведёт себя непредсказуемо.
 *
 * Разметка: у кнопки слайда адрес картинки лежит в `data-thumb-src`.
 * Само `<img>` создаёт и убирает эта лента.
 */

import { cachedSlideThumb, slideThumb } from "./slideThumbs";

/** Запас за краями ленты: соседний слайд грузится до того, как его домотают. */
const LOOKAHEAD_PX = 240;

/**
 * Потолок числа картинок в памяти. Должен быть заведомо больше, чем помещается
 * на экран: раньше стояло 10, и на высоком экране слайды с одиннадцатого
 * оставались пустыми — их было видно, но картинку им уже не выдавали.
 */
const MAX_LOADED = 60;

/**
 * Сколько раз переспрашиваем размер у ленты, пока она его не даст (по 250 мс).
 * Дальше ждём наблюдателя за размером: лента просто спрятана.
 */
const MAX_SIZE_TRIES = 40;

export type LazyThumbStrip = {
  /** Пересчитать после смены слайдов или адресов картинок. */
  refresh(): void;
  destroy(): void;
};

type Item = {
  el: HTMLElement;
  img: HTMLImageElement | null;
  /** Какой слайд сейчас должен быть в этой кнопке; `null` — картинка снята. */
  wanted: string | null;
};

function thumbSrc(el: HTMLElement): string | null {
  const src = el.dataset.thumbSrc;
  return src ? src : null;
}

function unload(item: Item): void {
  item.wanted = null;
  if (!item.img) return;
  // Сначала снимаем адрес: иначе Safari может оставить картинку в памяти,
  // пока не догрузит её до конца.
  item.img.removeAttribute("src");
  item.img.remove();
  item.img = null;
}

function show(item: Item, url: string): void {
  let img = item.img;
  if (!img) {
    img = document.createElement("img");
    img.alt = "";
    img.draggable = false;
    img.decoding = "async";
    item.el.appendChild(img);
    item.img = img;
  }
  if (img.getAttribute("src") !== url) img.src = url;
}

function load(item: Item, src: string): void {
  item.wanted = src;
  const ready = cachedSlideThumb(src);
  if (ready) {
    show(item, ready);
    return;
  }
  void slideThumb(src).then((url) => {
    // Пока делали копию, ленту могли промотать или сменить слайды.
    if (item.wanted !== src) return;
    // Уменьшить не вышло — показываем исходник, пустая карточка хуже.
    show(item, url ?? src);
  });
}

/**
 * Включает ленту на контейнере с кнопками слайдов.
 * `itemSelector` — по какому признаку искать кнопки внутри.
 */
export function createLazyThumbStrip(
  root: HTMLElement,
  itemSelector: string
): LazyThumbStrip {
  let items: Item[] = [];
  let frame = 0;
  let timer = 0;
  let sizeWait = 0;
  let sizeTries = 0;
  let destroyed = false;

  function collect(): void {
    const next: Item[] = [];
    for (const el of Array.from(root.querySelectorAll<HTMLElement>(itemSelector))) {
      const prev = items.find((it) => it.el === el);
      next.push(prev ?? { el, img: el.querySelector("img"), wanted: null });
    }
    // Кнопки, которых больше нет, освобождают свои картинки.
    for (const old of items) {
      if (!next.some((it) => it.el === old.el)) unload(old);
    }
    items = next;
  }

  function apply(): void {
    if (destroyed || !items.length) return;
    // Лента спрятана (на телефоне в свёрнутой трансляции) — картинки ей не нужны.
    //
    // Но нулевой размер бывает и просто «ещё не разложили»: первый пересчёт
    // идёт сразу после создания, когда ленту только вставили в документ.
    // Раньше мы на этом успокаивались, и лента оставалась пустой навсегда —
    // пересчёт заново никто не запускал. Теперь ждём размера и пробуем ещё.
    if (!root.clientWidth && !root.clientHeight) {
      for (const item of items) unload(item);
      waitForSize();
      return;
    }
    sizeTries = 0;
    const viewTop = root.scrollTop - LOOKAHEAD_PX;
    const viewBottom = root.scrollTop + root.clientHeight + LOOKAHEAD_PX;
    const viewLeft = root.scrollLeft - LOOKAHEAD_PX;
    const viewRight = root.scrollLeft + root.clientWidth + LOOKAHEAD_PX;

    let loaded = 0;
    for (const item of items) {
      const src = thumbSrc(item.el);
      const top = item.el.offsetTop;
      const left = item.el.offsetLeft;
      const visible =
        top < viewBottom &&
        top + item.el.offsetHeight > viewTop &&
        left < viewRight &&
        left + item.el.offsetWidth > viewLeft;

      if (src && visible && loaded < MAX_LOADED) {
        load(item, src);
        loaded += 1;
      } else {
        unload(item);
      }
    }
  }

  /**
   * Пока у ленты нет размера, переспрашиваем. Наблюдатель за размером сам
   * разбудит нас, когда лента раскроется; таймер — страховка для движков,
   * где наблюдателя нет, и для случая, когда размер дала не сама лента.
   */
  function waitForSize(): void {
    if (destroyed || sizeWait || sizeTries >= MAX_SIZE_TRIES) return;
    sizeTries += 1;
    sizeWait = window.setTimeout(() => {
      sizeWait = 0;
      if (destroyed) return;
      if (root.clientWidth || root.clientHeight) schedule();
      else waitForSize();
    }, 250);
  }

  function cancelScheduled(): void {
    if (frame) cancelAnimationFrame(frame);
    if (timer) window.clearTimeout(timer);
    frame = 0;
    timer = 0;
  }

  /**
   * Пересчёт откладываем до отрисовки, но не только на неё: в фоновой вкладке
   * (и в Safari просто при прокрутке страницы) кадры не идут вовсе, и лента
   * осталась бы пустой. Поэтому вторым путём — обычный таймер.
   */
  function schedule(): void {
    if (destroyed || frame || timer) return;
    const run = () => {
      cancelScheduled();
      apply();
    };
    frame = requestAnimationFrame(run);
    timer = window.setTimeout(run, 120);
  }

  root.addEventListener("scroll", schedule, { passive: true });

  // Лента меняет размер и когда её раскрывают, и когда доску масштабируют.
  const resizeWatch =
    typeof ResizeObserver === "function" ? new ResizeObserver(() => schedule()) : null;
  resizeWatch?.observe(root);

  collect();
  // Размеры кнопок известны только после раскладки — считаем на следующем кадре.
  schedule();

  return {
    refresh() {
      if (destroyed) return;
      collect();
      schedule();
    },
    destroy() {
      destroyed = true;
      cancelScheduled();
      if (sizeWait) window.clearTimeout(sizeWait);
      sizeWait = 0;
      resizeWatch?.disconnect();
      root.removeEventListener("scroll", schedule);
      for (const item of items) unload(item);
      items = [];
    },
  };
}
